import {
    DraggableModuleType,
    ModuleTypeSchema,
} from "@/types/modules";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const series = (fn: (i: number) => number) =>
    MONTHS.map((_, i) => Math.round(fn(i) * 10) / 10);

const CUSTOMER_NAMES = [
    "Stadtwerke Potsdam", "Klinikum Nordbayern", "Hansewerk Logistik", "Landratsamt Roth",
    "Bäckerei Kraus Gruppe", "Medi Labor Süd", "Autohaus Brandl", "TU Campus Services",
    "Brauerei Hofmann", "Spedition Lechner", "Kreissparkasse Mitte", "Fraunhofer Außenstelle",
    "Bürgerhospital", "Holzbau Wagner", "Stadtverwaltung Weiden", "Pflegedienst Sonnenhof",
    "Maschinenbau Ries", "Verkehrsbetriebe Ost", "Zahnklinik Am Park", "Agrarhandel Franken",
    "Schulzentrum West", "Optik Seidl", "Wasserverband Altmühl", "Kanzlei Berger und Roth",
];

const CUSTOMER_SECTORS = [
    "Public sector", "Healthcare", "Logistics", "Public sector", "Retail", "Healthcare", "Retail",
    "Education", "Manufacturing", "Logistics", "Finance", "Research", "Healthcare", "Manufacturing",
    "Public sector", "Healthcare", "Manufacturing", "Public sector", "Healthcare", "Retail",
    "Education", "Retail", "Public sector", "Services",
];

const CONTRACTS = ["Managed", "Project", "Mixed"] as const;

export const DEMO_MODULES: Record<DraggableModuleType, any> = {
    bar: {
        type: "bar",
        title: "Bar Chart",
        xAxisLabel: "Quarter",
        yAxisLabel: "Amount",
        data: [
            { name: "Q1", revenue: 4000, costs: 2400 },
            { name: "Q2", revenue: 3000, costs: 1398 },
            { name: "Q3", revenue: 5000, costs: 3200 },
            { name: "Q4", revenue: 4780, costs: 2908 },
        ],
        series: [
            { key: "revenue", label: "Revenue" },
            { key: "costs", label: "Costs" },
        ],
    },
    line: {
        type: "line",
        title: "Line Chart",
        xAxisLabel: "Month",
        yAxisLabel: "Count",
        data: [
            { name: "Jan", users: 400, sessions: 240 },
            { name: "Feb", users: 600, sessions: 380 },
            { name: "Mar", users: 800, sessions: 520 },
            { name: "Apr", users: 1200, sessions: 780 },
            { name: "May", users: 1600, sessions: 1020 },
        ],
        series: [
            { key: "users", label: "Users" },
            { key: "sessions", label: "Sessions" },
        ],
    },
    pie: {
        type: "pie",
        title: "Pie Chart",
        data: [
            { name: "Product A", value: 400 },
            { name: "Product B", value: 300 },
            { name: "Product C", value: 200 },
            { name: "Product D", value: 100 },
        ],
    },
    scatter: {
        type: "scatter",
        title: "Scatter Chart",
        xAxisLabel: "Weight",
        xAxisUnit: "kg",
        yAxisLabel: "Height",
        yAxisUnit: "cm",
        data: {
            series: [
                {
                    name: "Group A",
                    data: [
                        { x: 60, y: 165 },
                        { x: 72, y: 175 },
                        { x: 55, y: 160 },
                        { x: 80, y: 180 },
                    ],
                },
                {
                    name: "Group B",
                    data: [
                        { x: 68, y: 170 },
                        { x: 90, y: 190 },
                        { x: 78, y: 178 },
                    ],
                },
            ],
        },
    },
    area: {
        type: "area",
        title: "Area Chart",
        xAxisLabel: "Month",
        yAxisLabel: "Amount",
        data: [
            { name: "Jan", revenue: 4000, expenses: 2400 },
            { name: "Feb", revenue: 4200, expenses: 2100 },
            { name: "Mar", revenue: 5100, expenses: 2600 },
            { name: "Apr", revenue: 5900, expenses: 3200 },
            { name: "May", revenue: 6300, expenses: 3400 },
        ],
        series: [
            { key: "revenue", label: "Revenue" },
            { key: "expenses", label: "Expenses" },
        ],
    },
    progress: {
        type: "progress",
        title: "Progress Bars",
        data: {
            variant: "progress",
            items: [
                { id: "development", label: "Development", value: 75, subtext: "75 of 100 tasks done", status: "on-track" },
                { id: "milestones", label: "Milestones", value: 60, subtext: "3 of 5 reached", status: "on-track" },
                { id: "testing", label: "Testing", value: 90, subtext: "90 of 100 cases green", status: "on-track" },
            ],
        },
    },
    quote: {
        type: "quote",
        title: "Quote",
        data: {
            quote: "This tool transformed how we share knowledge with our audience.",
            author: "Jane Smith",
            role: "Head of Marketing",
        },
    },
    image: {
        type: "image",
        data: {
            src: "/images/previewTest.jpg",
            alt: "Preview image",
            description: "Sample presentation slide",
        },
    },
    pyramid: {
        type: "pyramid",
        title: "Maturity Pyramid",
        variant: "nodes",
        data: {
            tiers: [
                { id: "optimizing", label: "Optimizing", value: "12%", status: "pending" },
                { id: "managed",    label: "Managed",    value: "28%", status: "pending" },
                { id: "defined",    label: "Defined",    value: "45%", status: "active"  },
                { id: "repeatable", label: "Repeatable", value: "72%", status: "done"    },
                { id: "initial",    label: "Initial",    value: "94%", status: "done"    },
            ],
        },
    },
    "dotted-map": {
        type: "dotted-map",
        title: "Dotted Map",
        data: {
            pins: [
                { lat: 52.52, lng: 13.405, label: "Berlin" },
                { lat: 40.7128, lng: -74.006, label: "New York" },
                { lat: 35.6762, lng: 139.6503, label: "Tokyo" },
                { lat: -33.8688, lng: 151.2093, label: "Sydney" },
            ],
        },
    },
    "radial-bar": {
        type: "radial-bar",
        title: "Radial Bar",
        data: [
            { name: "Adoption", value: 82 },
            { name: "Retention", value: 68 },
            { name: "Satisfaction", value: 91 },
            { name: "Referrals", value: 47 },
        ],
    },
    candlestick: {
        type: "candlestick",
        title: "Candlestick Chart",
        xAxisLabel: "January 2025",
        yAxisLabel: "Share price",
        data: {
            points: [
                { date: "2025-01-01", open: 100, high: 108, low: 96, close: 104 },
                { date: "2025-01-02", open: 104, high: 112, low: 101, close: 109 },
                { date: "2025-01-03", open: 109, high: 111, low: 102, close: 103 },
                { date: "2025-01-04", open: 103, high: 107, low: 99, close: 106 },
                { date: "2025-01-05", open: 106, high: 114, low: 105, close: 112 },
                { date: "2025-01-06", open: 112, high: 115, low: 108, close: 110 },
                { date: "2025-01-07", open: 110, high: 118, low: 109, close: 116 },
            ],
        },
    },
    funnel: {
        type: "funnel",
        title: "Funnel Chart",
        data: {
            stages: [
                { label: "Visitors", value: 12400, displayValue: "12.4k" },
                { label: "Leads", value: 6800, displayValue: "6.8k" },
                { label: "Qualified", value: 3200, displayValue: "3.2k" },
                { label: "Proposals", value: 1500, displayValue: "1.5k" },
                { label: "Closed", value: 620, displayValue: "620" },
            ],
        },
    },
    heatmap: {
        type: "heatmap",
        title: "Heatmap",
        data: {
            points: (() => {
                const pts: { x: number; y: number; value: number }[] = [];
                for (let week = 0; week < 12; week++) {
                    for (let day = 0; day < 7; day++) {
                        pts.push({
                            x: week,
                            y: day,
                            value: (week * 7 + day) % 5,
                        });
                    }
                }
                return pts;
            })(),
        },
    },
    "profit-loss": {
        type: "profit-loss",
        title: "Profit / Loss",
        xAxisLabel: "January 2025",
        yAxisLabel: "Net result",
        data: {
            points: [
                { date: "2025-01-01", value: 420 },
                { date: "2025-01-02", value: -180 },
                { date: "2025-01-03", value: 260 },
                { date: "2025-01-04", value: -95 },
                { date: "2025-01-05", value: 340 },
                { date: "2025-01-06", value: 180 },
                { date: "2025-01-07", value: -240 },
                { date: "2025-01-08", value: 520 },
            ],
        },
    },
    radar: {
        type: "radar",
        title: "Radar Chart",
        data: {
            metrics: [
                { key: "speed", label: "Speed" },
                { key: "power", label: "Power" },
                { key: "accuracy", label: "Accuracy" },
                { key: "endurance", label: "Endurance" },
                { key: "reach", label: "Reach" },
            ],
            series: [
                {
                    label: "Player A",
                    values: { speed: 85, power: 70, accuracy: 78, endurance: 90, reach: 65 },
                },
                {
                    label: "Player B",
                    values: { speed: 68, power: 88, accuracy: 62, endurance: 74, reach: 82 },
                },
            ],
        },
    },
    sankey: {
        type: "sankey",
        title: "Sankey Diagram",
        data: {
            nodes: [
                { name: "Organic Search", category: "source" as const },
                { name: "Paid Ads", category: "source" as const },
                { name: "Direct", category: "source" as const },
                { name: "Homepage", category: "landing" as const },
                { name: "Product Page", category: "landing" as const },
                { name: "Signed Up", category: "outcome" as const },
                { name: "Bounced", category: "outcome" as const },
            ],
            links: [
                { source: 0, target: 3, value: 120 },
                { source: 0, target: 4, value: 60 },
                { source: 1, target: 4, value: 80 },
                { source: 2, target: 3, value: 40 },
                { source: 3, target: 5, value: 90 },
                { source: 3, target: 6, value: 70 },
                { source: 4, target: 5, value: 100 },
                { source: 4, target: 6, value: 40 },
            ],
        },
    },
    sunburst: {
        type: "sunburst",
        title: "Sunburst",
        data: {
            name: "Revenue",
            children: [
                {
                    name: "Product",
                    children: [
                        { name: "Enterprise", value: 198 },
                        { name: "Pro", value: 145 },
                        { name: "Starter", value: 62 },
                    ],
                },
                {
                    name: "Services",
                    children: [
                        { name: "Consulting", value: 88 },
                        { name: "Training", value: 42 },
                    ],
                },
                {
                    name: "Partnerships",
                    children: [
                        { name: "Referrals", value: 55 },
                        { name: "Resellers", value: 38 },
                    ],
                },
            ],
        },
    },
    "progress-stages": {
        type: "progress-stages",
        title: "Delivery pipeline",
        data: {
            stages: [
                { id: "discovery", label: "Discovery", detail: "Scope defined", status: "done" as const },
                { id: "design", label: "Design", detail: "Approved", status: "done" as const },
                { id: "build", label: "Build", detail: "In progress", status: "active" as const },
                { id: "test", label: "Test", detail: "Not started", status: "pending" as const },
                { id: "ship", label: "Ship", detail: "Not started", status: "pending" as const },
            ],
        },
    },
    "fork-join": {
        type: "fork-join",
        title: "Parallel workflow",
        data: {
            nodes: [
                { id: "start", label: "Trigger", x: 5, y: 1, status: "done" as const },
                { id: "init", label: "Initialize", x: 20, y: 1, status: "done" as const },
                { id: "fork", label: "Fork", x: 34, y: 1, status: "done" as const, type: "junction" as const },
                { id: "a1", label: "Fetch", x: 55, y: 0.2, status: "active" as const },
                { id: "b1", label: "Validate", x: 55, y: 1.8, status: "active" as const },
                { id: "join", label: "Merge", x: 78, y: 1, status: "pending" as const, type: "junction" as const },
                { id: "final", label: "Deliver", x: 95, y: 1, status: "pending" as const },
            ],
            edges: [
                ["start", "init"],
                ["init", "fork"],
                ["fork", "a1"],
                ["fork", "b1"],
                ["a1", "join"],
                ["b1", "join"],
                ["join", "final"],
            ] as [string, string][],
        },
    },
    "gantt-phases": {
        type: "gantt-phases",
        title: "Project plan",
        data: {
            phases: [
                { id: "planning", name: "Planning", start: 0, duration: 8, progress: 100, color: "var(--chart-1)" },
                { id: "design", name: "Design", start: 8, duration: 12, progress: 100, color: "var(--chart-2)" },
                { id: "build", name: "Build", start: 18, duration: 18, progress: 60, color: "var(--chart-3)" },
                { id: "qa", name: "QA", start: 34, duration: 8, progress: 10, color: "var(--chart-4)" },
                { id: "launch", name: "Launch", start: 40, duration: 4, progress: 0, color: "var(--chart-5)" },
            ],
            totalDays: 45,
            currentDay: 22,
            dayLabels: [0, 7, 14, 21, 28, 35, 42],
        },
    },
    "parallel-streams": {
        type: "parallel-streams",
        title: "Workstreams",
        data: {
            tracks: [
                {
                    id: "frontend",
                    name: "Frontend",
                    color: "var(--chart-1)",
                    steps: [
                        { name: "Setup", pos: 5, status: "done" as const },
                        { name: "Build", pos: 45, status: "active" as const },
                        { name: "Ship", pos: 90, status: "pending" as const },
                    ],
                },
                {
                    id: "backend",
                    name: "Backend",
                    color: "var(--chart-2)",
                    steps: [
                        { name: "API", pos: 10, status: "done" as const },
                        { name: "Auth", pos: 50, status: "active" as const },
                        { name: "Deploy", pos: 92, status: "pending" as const },
                    ],
                },
            ],
            milestones: [
                { pos: 30, label: "M1", subLabel: "Kick-off" },
                { pos: 95, label: "M2", subLabel: "Launch" },
            ],
        },
    },
    "segmented-progress": {
        type: "segmented-progress",
        title: "Weighted phases",
        data: {
            segments: [
                { id: "discovery", name: "Discovery", weight: 15, progress: 100, color: "var(--chart-1)" },
                { id: "design", name: "Design", weight: 25, progress: 100, color: "var(--chart-2)" },
                { id: "build", name: "Build", weight: 35, progress: 60, color: "var(--chart-3)" },
                { id: "qa", name: "QA", weight: 15, progress: 0, color: "var(--chart-4)" },
                { id: "ship", name: "Ship", weight: 10, progress: 0, color: "var(--chart-5)" },
            ],
        },
    },
    "state-machine": {
        type: "state-machine",
        title: "Approval flow",
        data: {
            states: [
                { id: "draft", label: "Draft", x: 12, y: 1, status: "done" as const },
                { id: "review", label: "Review", x: 40, y: 1, status: "active" as const },
                { id: "approved", label: "Approved", x: 70, y: 1, status: "pending" as const },
                { id: "rejected", label: "Rejected", x: 40, y: 2, status: "pending" as const },
                { id: "published", label: "Published", x: 92, y: 1, status: "pending" as const },
            ],
            transitions: [
                { from: "draft", to: "review", label: "submit", type: "normal" as const },
                { from: "review", to: "approved", label: "approve", type: "normal" as const },
                { from: "approved", to: "published", label: "publish", type: "normal" as const },
                { from: "review", to: "rejected", label: "reject", type: "negative" as const },
                { from: "rejected", to: "draft", label: "revise", type: "retry" as const },
            ],
        },
    },
    "timeline-stepper": {
        type: "timeline-stepper",
        title: "Release schedule",
        data: {
            steps: [
                { id: 1, name: "Kickoff", status: "done" as const, date: "Sep 01" },
                { id: 2, name: "Discovery", status: "done" as const, date: "Sep 03" },
                { id: 3, name: "Design", status: "done" as const, date: "Sep 08" },
                { id: 4, name: "Build", status: "active" as const, date: "Sep 16" },
                { id: 5, name: "Review", status: "pending" as const, date: "Sep 30" },
                { id: 6, name: "Launch", status: "pending" as const, date: "Oct 04" },
            ],
        },
    },
    "quadrant-matrix": {
        type: "quadrant-matrix",
        title: "Effort vs. impact",
        data: {
            xLabel: "Effort",
            yLabel: "Impact",
            items: [
                { id: "1", name: "Fix login bug", effort: 15, impact: 30 },
                { id: "2", name: "Add SSO", effort: 42, impact: 82 },
                { id: "3", name: "Rewrite billing", effort: 85, impact: 88 },
                { id: "4", name: "Dark mode", effort: 22, impact: 48 },
                { id: "5", name: "Onboarding v2", effort: 62, impact: 78 },
                { id: "6", name: "Legacy migration", effort: 90, impact: 32 },
                { id: "7", name: "Feature toggles", effort: 32, impact: 65 },
                { id: "8", name: "Search redesign", effort: 65, impact: 42 },
            ],
        },
    },
    "correlation-heatmap": {
        type: "correlation-heatmap",
        title: "Feature correlations",
        data: {
            features: ["Freq", "Recency", "Depth", "Session", "Retention", "Referral"],
            matrix: [
                [1.0, 0.72, 0.58, 0.41, 0.65, 0.23],
                [0.72, 1.0, 0.44, 0.38, 0.81, 0.15],
                [0.58, 0.44, 1.0, 0.62, 0.53, 0.21],
                [0.41, 0.38, 0.62, 1.0, 0.37, 0.09],
                [0.65, 0.81, 0.53, 0.37, 1.0, 0.28],
                [0.23, 0.15, 0.21, 0.09, 0.28, 1.0],
            ],
        },
    },
    "feature-comparison": {
        type: "feature-comparison",
        title: "Plan comparison",
        data: {
            features: [
                { name: "Team members", values: ["3", "10", "50", "Unlimited"] },
                { name: "Storage", values: ["1 GB", "10 GB", "500 GB", "5 TB"] },
                { name: "SSO", values: [false, false, true, true] },
                { name: "Priority support", values: [false, false, true, true] },
                { name: "SLA", values: [false, false, "99.5%", "99.99%"] },
            ],
            plans: [
                { name: "Free", price: "€0" },
                { name: "Pro", price: "€29/mo" },
                { name: "Business", price: "€99/mo", highlight: true },
                { name: "Enterprise", price: "Custom" },
            ],
        },
    },
    "ring-kpis": {
        type: "ring-kpis",
        title: "Service KPIs",
        data: {
            deltaPeriod: "vs last month",
            previousLabel: "Last month",
            items: [
                { id: "csat", label: "Customer satisfaction", value: 4.6, previous: 4.3, max: 5, digits: 1, unit: "of 5" },
                { id: "util", label: "Utilisation", value: 82, previous: 77, max: 100, format: "percent" },
                { id: "resp", label: "Response time", value: 38, previous: 46, progress: 62, unit: "min", invertDelta: true },
                { id: "renew", label: "Renewal rate", value: 91, previous: 93, max: 100, format: "percent" },
            ],
        },
    },
    "metric-tabs-chart": {
        type: "metric-tabs-chart",
        title: "Business development",
        data: {
            periods: MONTHS,
            compareLabel: "Previous year",
            metrics: [
                {
                    id: "revenue",
                    label: "Revenue",
                    format: "eur",
                    values: series((i) => 92000 + i * 4200 + ((i * 37) % 11) * 1500),
                    compareValues: series((i) => 84000 + i * 2600 + ((i * 23) % 9) * 1400),
                },
                {
                    id: "customers",
                    label: "New customers",
                    values: series((i) => 4 + ((i * 5) % 6)),
                    compareValues: series((i) => 3 + ((i * 7) % 5)),
                },
                {
                    id: "utilisation",
                    label: "Utilisation",
                    aggregate: "average",
                    format: "percent",
                    digits: 0,
                    values: series((i) => 74 + ((i * 3) % 9) + i * 0.5),
                    compareValues: series((i) => 71 + ((i * 5) % 8)),
                },
                {
                    id: "churn",
                    label: "Churn rate",
                    aggregate: "average",
                    format: "percent",
                    digits: 1,
                    values: series((i) => Math.max(0.6, 2.4 - i * 0.12 + ((i * 3) % 4) * 0.15)),
                    compareValues: series((i) => 2.6 + ((i * 2) % 3) * 0.2),
                },
            ],
        },
    },
    breakdown: {
        type: "breakdown",
        title: "Revenue breakdown",
        data: {
            label: "Revenue by product line, 2026",
            format: "eur",
            items: [
                { id: "network", name: "Managed network", value: 512000 },
                { id: "security", name: "Security services", value: 318000 },
                { id: "cloud", name: "Cloud operations", value: 241000 },
                { id: "consulting", name: "Consulting and projects", value: 137000 },
                { id: "support", name: "Support contracts", value: 76000 },
            ],
            footnote: "Net revenue, excluding hardware resale.",
        },
    },
    "dual-metric": {
        type: "dual-metric",
        title: "Revenue mix",
        data: {
            label: "Recurring vs. project revenue",
            periods: [
                {
                    id: "2026",
                    label: "2026",
                    metrics: [
                        { id: "recurring", label: "Recurring revenue", value: 486000, format: "eur", detail: { label: "Largest driver", value: "Network operations, 61 %", description: "Share of recurring revenue that comes from contracts for ongoing network operations." } },
                        { id: "project", label: "Project business", value: 798000, format: "eur", detail: { label: "Largest project", value: "Klinikum Nordbayern" } },
                    ],
                },
                {
                    id: "2025",
                    label: "2025",
                    metrics: [
                        { id: "recurring", label: "Recurring revenue", value: 348000, format: "eur", detail: { label: "Largest driver", value: "Network operations, 54 %", description: "Share of recurring revenue that comes from contracts for ongoing network operations." } },
                        { id: "project", label: "Project business", value: 768000, format: "eur", detail: { label: "Largest project", value: "Stadtwerke Potsdam" } },
                    ],
                },
                {
                    id: "2030",
                    label: "Target 2030",
                    metrics: [
                        { id: "recurring", label: "Recurring revenue", value: 1100000, format: "eur", detail: { label: "Planned drivers", value: "5 standard products" } },
                        { id: "project", label: "Project business", value: 1100000, format: "eur", detail: { label: "Role", value: "Entry into operations contracts" } },
                    ],
                },
            ],
        },
    },
    "stage-bars": {
        type: "stage-bars",
        title: "Sales pipeline",
        data: {
            label: "Enquiries this period",
            ranges: [
                {
                    id: "q3",
                    label: "Q3 2026",
                    stages: [
                        { id: "enquiries", name: "Enquiries", value: 1240 },
                        { id: "qualified", name: "Qualified", value: 612 },
                        { id: "offer", name: "Offer", value: 284 },
                        { id: "negotiation", name: "Negotiation", value: 131 },
                        { id: "won", name: "Won", value: 74 },
                    ],
                },
                {
                    id: "q2",
                    label: "Q2 2026",
                    stages: [
                        { id: "enquiries", name: "Enquiries", value: 1051 },
                        { id: "qualified", name: "Qualified", value: 488 },
                        { id: "offer", name: "Offer", value: 236 },
                        { id: "negotiation", name: "Negotiation", value: 102 },
                        { id: "won", name: "Won", value: 51 },
                    ],
                },
                {
                    id: "h1",
                    label: "H1 2026",
                    stages: [
                        { id: "enquiries", name: "Enquiries", value: 2042 },
                        { id: "qualified", name: "Qualified", value: 917 },
                        { id: "offer", name: "Offer", value: 431 },
                        { id: "negotiation", name: "Negotiation", value: 188 },
                        { id: "won", name: "Won", value: 96 },
                    ],
                },
            ],
        },
    },
    "insight-panel": {
        type: "insight-panel",
        title: "Strategy 2030",
        data: {
            badge: "Key insights",
            items: [
                { id: "recurring", value: 50, format: "percent", headline: "Half of all revenue is to be recurring by 2030.", description: "In 2026 the share is 38 %, in 2025 it was 31 %." },
                { id: "workshops", value: 9, headline: "Workshops with every team shaped the strategy.", description: "92 colleagues took part, and 7 topic blocks came out of it." },
                { id: "products", value: 5, headline: "Standard products replace one-off projects.", description: "Every product has a fixed scope and a monthly price." },
            ],
        },
    },
    table: {
        type: "table",
        title: "Customers",
        data: {
            columns: [
                { id: "name", label: "Customer" },
                { id: "sector", label: "Sector" },
                { id: "contract", label: "Contract", format: "tag" },
                { id: "revenue", label: "Revenue 2026", format: "eur" },
                { id: "growth", label: "Growth", format: "percent", digits: 1 },
            ],
            rows: CUSTOMER_NAMES.map((name, i) => ({
                id: `c${i + 1}`,
                cells: [
                    name,
                    CUSTOMER_SECTORS[i],
                    CONTRACTS[(i * 7) % 3],
                    Math.round((180 - i * 6.3 + ((i * 37) % 23)) * 1000),
                    ((i * 53) % 41) - 12,
                ],
            })),
        },
    },
    "skill-level-matrix": {
        type: "skill-level-matrix",
        title: "Team skill map",
        data: {
            rowLabels: ["TypeScript", "Python", "Go", "SQL", "AWS", "Docker"],
            colLabels: ["Alex", "Ben", "Cara", "Dan", "Eli"],
            levels: [
                [4, 3, 4, 5, 2],
                [5, 4, 2, 3, 4],
                [3, 2, 1, 4, 0],
                [4, 5, 3, 4, 5],
                [3, 4, 5, 3, 4],
                [4, 4, 5, 4, 3],
            ],
            maxLevel: 5,
        },
    },
    "node-cycle": {
        type: "node-cycle",
        title: "Product cadence",
        data: {
            phases: [
                { id: "discover", label: "Discover", status: "done" as const,    duration: "3d" },
                { id: "design",   label: "Design",   status: "done" as const,    duration: "5d" },
                { id: "build",    label: "Build",    status: "active" as const,  duration: "8d" },
                { id: "test",     label: "Test",     status: "pending" as const, duration: "3d" },
                { id: "ship",     label: "Ship",     status: "pending" as const, duration: "1d" },
                { id: "measure",  label: "Measure",  status: "pending" as const, duration: "5d" },
            ],
        },
    },
    "segmented-cycle": {
        type: "segmented-cycle",
        title: "Product cadence",
        data: {
            phases: [
                { id: "discover", label: "Discover", status: "done" as const,    duration: "3d" },
                { id: "design",   label: "Design",   status: "done" as const,    duration: "5d" },
                { id: "build",    label: "Build",    status: "active" as const,  duration: "8d" },
                { id: "test",     label: "Test",     status: "pending" as const, duration: "3d" },
                { id: "ship",     label: "Ship",     status: "pending" as const, duration: "1d" },
                { id: "measure",  label: "Measure",  status: "pending" as const, duration: "5d" },
            ],
        },
    },
    "ecosystem-map": {
        type: "ecosystem-map",
        title: "Platform ecosystem",
        data: {
            core: { lines: ["Operations", "Service"] },
            foundation: {
                label: "Foundation",
                segments: [
                    { id: "connectivity", label: "Systema Connectivity", status: "active" as const },
                    { id: "product",      label: "Systema Product",       status: "active" as const },
                    { id: "ucc",          label: "Systema UCC",           status: "inactive" as const },
                ],
            },
            products: {
                label: "Products",
                segments: [
                    { id: "ai",       label: "Systema AI",       status: "active" as const },
                    { id: "cases",    label: "Systema Cases",    status: "inactive" as const },
                    { id: "engine",   label: "Systema Engine",   status: "active" as const },
                    { id: "data",     label: "Systema Data",     status: "inactive" as const },
                    { id: "storage",  label: "Systema Storage",  status: "inactive" as const },
                    { id: "cloud",    label: "Systema Cloud",    status: "inactive" as const },
                    { id: "multi",    label: "Systema Multi",    status: "active" as const },
                    { id: "watch",    label: "Systema Watch",    status: "inactive" as const },
                ],
            },
            marketplace: { label: "Systema Apps  ·  Marketplace" },
        },
    },
    "hierarchy-tree": {
        type: "hierarchy-tree",
        title: "Org chart",
        data: {
            variant: "columnar" as const,
            nodes: [
                { id: "root",  label: "Organization", parent: null,   level: 0 },
                { id: "prod",  label: "Product",      parent: "root", level: 1 },
                { id: "eng",   label: "Engineering",  parent: "root", level: 1 },
                { id: "ops",   label: "Operations",   parent: "root", level: 1 },
                { id: "ios",   label: "iOS",          parent: "prod", level: 2 },
                { id: "web",   label: "Web",          parent: "prod", level: 2 },
                { id: "des",   label: "Design",       parent: "prod", level: 2 },
                { id: "plat",  label: "Platform",     parent: "eng",  level: 2 },
                { id: "ml",    label: "ML",           parent: "eng",  level: 2 },
                { id: "dev",   label: "DevOps",       parent: "eng",  level: 2 },
                { id: "fin",   label: "Finance",      parent: "ops",  level: 2 },
                { id: "ppl",   label: "People",       parent: "ops",  level: 2 },
            ],
        },
    },
    "parallel-category-trees": {
        type: "parallel-category-trees",
        title: "Product vs Engineering",
        data: {
            nodes: [
                { id: "prod",  label: "Product",      parent: null,   level: 0 },
                { id: "eng",   label: "Engineering",  parent: null,   level: 0 },
                { id: "ios",   label: "iOS",          parent: "prod", level: 1 },
                { id: "web",   label: "Web",          parent: "prod", level: 1 },
                { id: "des",   label: "Design",       parent: "prod", level: 1 },
                { id: "plat",  label: "Platform",     parent: "eng",  level: 1 },
                { id: "ml",    label: "ML",           parent: "eng",  level: 1 },
                { id: "dev",   label: "DevOps",       parent: "eng",  level: 1 },
            ],
            rootIds: ["prod", "eng"] as [string, string],
        },
    },
    "delta-comparison": {
        type: "delta-comparison",
        title: "Metric deltas",
        data: {
            metrics: [
                { id: 1, label: "MRR", values: [{ value: "€10,240" }, { value: "€12,480", highlighted: true }], delta: 21.8, sentiment: "good" as const },
                { id: 2, label: "Active users", values: [{ value: "8,120" }, { value: "8,920", highlighted: true }], delta: 9.9, sentiment: "good" as const },
                { id: 3, label: "Response time", values: [{ value: "92ms" }, { value: "115ms" }, { value: "128ms", highlighted: true }], delta: 39.1, sentiment: "bad" as const },
                { id: 4, label: "Sign-ups", values: [{ value: "128" }, { value: "132" }, { value: "138" }, { value: "142", highlighted: true }], delta: 10.9, sentiment: "good" as const },
                { id: 5, label: "Conversion", values: [{ value: "3.4%", highlighted: true }, { value: "3.2%" }], delta: -5.9, sentiment: "bad" as const },
                { id: 6, label: "Page views", values: [{ value: "34,200" }, { value: "36,500" }, { value: "38,900", highlighted: true }], delta: 13.7, sentiment: "good" as const },
            ],
        },
    },
    "split-comparison": {
        type: "split-comparison",
        title: "Team Alpha vs Team Beta",
        data: {
            entities: [
                { id: "alpha", name: "Team Alpha", tag: "5 members · Q3" },
                { id: "beta",  name: "Team Beta",  tag: "6 members · Q3" },
            ],
            metrics: [
                { id: "deploys",  label: "Deploys",       higherIsBetter: true,  values: { alpha: 142,  beta: 128 } },
                { id: "prs",      label: "Pull requests", higherIsBetter: true,  values: { alpha: 89,   beta: 124 } },
                { id: "coverage", label: "Test coverage", unit: "%", higherIsBetter: true,  values: { alpha: 94.2, beta: 91.8 } },
                { id: "inc",      label: "Incidents",    higherIsBetter: false, values: { alpha: 3,    beta: 5 } },
                { id: "oncall",   label: "On-call hours", unit: "h", higherIsBetter: false, values: { alpha: 48,   beta: 52 } },
                { id: "cycle",    label: "Cycle time",    unit: "d", higherIsBetter: false, values: { alpha: 3.2,  beta: 2.4 } },
            ],
        },
    },
    "metric-list": {
        type: "metric-list",
        title: "Key metrics",
        data: {
            metrics: [
                { id: 1, icon: "euro" as const,            tint: "var(--chart-1)", label: "MRR",           value: "€12,480", trend: "up" as const,   pct: 8.2  },
                { id: 2, icon: "users" as const,           tint: "var(--chart-2)", label: "Active users",  value: "8,920",   trend: "up" as const,   pct: 3.4  },
                { id: 3, icon: "user-plus" as const,       tint: "var(--chart-2)", label: "Sign-ups",      value: "142",     trend: "up" as const,   pct: 12.5 },
                { id: 4, icon: "activity" as const,        tint: "var(--chart-2)", label: "Conversion",    value: "3.2%",    trend: "down" as const, pct: 0.4  },
                { id: 5, icon: "clock" as const,           tint: "var(--chart-3)", label: "Avg session",   value: "4m 12s",  trend: "flat" as const, pct: 0.1  },
                { id: 6, icon: "globe" as const,           tint: "var(--chart-2)", label: "Bounce rate",   value: "42.8%",   trend: "down" as const, pct: 1.7  },
                { id: 7, icon: "zap" as const,             tint: "var(--emerald)", label: "Response time", value: "128ms",   trend: "down" as const, pct: 8.3  },
                { id: 8, icon: "check-circle" as const,    tint: "var(--emerald)", label: "Uptime",        value: "99.94%",  trend: "up" as const,   pct: 0.02 },
                { id: 9, icon: "alert-triangle" as const,  tint: "var(--rose)",    label: "API errors",    value: "0.3%",    trend: "up" as const,   pct: 0.1  },
            ],
        },
    },
    "activity-feed": {
        type: "activity-feed",
        title: "Recent activity",
        data: {
            items: [
                { id: 1, icon: "git-commit" as const,     tint: "var(--chart-1)", actor: "Manu", verb: "deployed",     target: "ragistry-mvp",       detail: "commit a3f8c92 — auth refactor",             time: "2m"  },
                { id: 2, icon: "pencil" as const,         tint: "var(--chart-2)", actor: "Alex", verb: "updated",      target: "deploy.yml",         detail: "changed environment variables for staging",  time: "14m" },
                { id: 3, icon: "message-circle" as const, tint: "var(--chart-2)", actor: "Sara", verb: "commented on", target: "#ISS-247",           detail: "\"Let's ship after the design review\"",      time: "1h"  },
                { id: 4, icon: "check-circle" as const,   tint: "var(--emerald)", actor: "Manu", verb: "resolved",     target: "#ISS-201",           detail: "onboarding flow handles empty state",        time: "3h"  },
                { id: 5, icon: "user-plus" as const,      tint: "var(--chart-1)", actor: "Manu", verb: "invited",      target: "sara@team.dev",      detail: "added as Admin",                             time: "5h"  },
                { id: 6, icon: "plus" as const,           tint: "var(--chart-1)", actor: "Manu", verb: "created",      target: "workspace",          detail: "initialized with default settings",          time: "1d"  },
                { id: 7, icon: "trash-2" as const,        tint: "var(--chart-3)", actor: "Ben",  verb: "removed",      target: "feature/old-search", detail: "merged into main, branch deleted",           time: "2d"  },
                { id: 8, icon: "git-commit" as const,     tint: "var(--chart-1)", actor: "Alex", verb: "deployed",     target: "handyout",           detail: "commit b1d2e05 — mobile fixes",              time: "2d"  },
                { id: 9, icon: "message-circle" as const, tint: "var(--chart-2)", actor: "Sara", verb: "opened",       target: "#ISS-252",           detail: "reported dashboard chart empty state",       time: "3d"  },
            ],
        },
    },
    "social-proof-stats": {
        type: "social-proof-stats",
        title: "Social proof stats",
        data: {
            stats: [
                {
                    id: "companies",
                    label: "Companies",
                    value: "500+",
                    caption: "12% growth vs. last month",
                    visual: {
                        kind: "avatars" as const,
                        people: [
                            { initials: "DL", shade: 18 },
                            { initials: "AC", shade: 20 },
                            { initials: "NX", shade: 22 },
                            { initials: "RV", shade: 20 },
                            { initials: "ML", shade: 18 },
                        ],
                        extraCount: 495,
                    },
                },
                {
                    id: "docs",
                    label: "Docs / day",
                    value: "2.4M",
                    caption: "Peak of 3.1M on Nov 14",
                    visual: {
                        kind: "bars" as const,
                        data: [12, 18, 22, 35, 48, 62, 78, 92, 88, 65, 42, 28],
                    },
                },
                {
                    id: "latency",
                    label: "P95 latency",
                    value: "142ms",
                    caption: "8.2% lower than last quarter",
                    visual: {
                        kind: "spark" as const,
                        data: [180, 175, 172, 168, 165, 158, 155, 152, 148, 145, 143, 142],
                    },
                },
            ],
        },
    },
    "list-cards": {
        type: "list-cards",
        title: "List cards",
        data: {
            cards: [
                {
                    icon: "zap" as const,
                    heading: "Instant handyouts",
                    text: "Upload a PPTX and get a shareable handyout at about ten seconds a slide — no cleanup, no reformatting.",
                },
                {
                    icon: "eye" as const,
                    heading: "Live engagement",
                    text: "See who read what, how far they scrolled, and where attention dropped off — in real time.",
                },
                {
                    icon: "shield-check" as const,
                    heading: "Enterprise-grade",
                    text: "GDPR-compliant hosting in the EU, SSO for teams, and audit logs on every viewer session.",
                },
            ],
        },
    },
    "big-number-chart": {
        type: "big-number-chart",
        title: "Big number chart",
        data: {
            series: [52, 55, 54, 61, 63, 60, 68, 71, 74, 79, 83, 87],
            baseline: 64,
            metricLabel: "Open rate",
            description:
                "of attendees open the handout within 24 hours of the talk.",
            unit: "%",
            periodLabel: "Week",
            periodLabelPlural: "weeks",
            seriesLabel: "Handout",
            baselineLabel: "PDF sent by email",
            deltaUnit: "percentage points",
        },
    },
    "pricing-tiers": {
        type: "pricing-tiers",
        title: "Pricing tiers",
        data: {
            tiers: [
                {
                    id: "starter",
                    name: "Starter",
                    description: "For individual speakers",
                    price: 10,
                    included: [
                        "Unlimited handouts",
                        "QR code and link sharing",
                        "Basic analytics",
                    ],
                },
                {
                    id: "pro",
                    name: "Pro",
                    description: "For sales teams",
                    price: 30,
                    included: [
                        "Unlimited handouts",
                        "QR code and link sharing",
                        "Basic analytics",
                        "Scroll depth and reading time",
                        "Custom branding",
                        "Feedback module",
                    ],
                    recommended: true,
                },
                {
                    id: "business",
                    name: "Business",
                    description: "For entire organizations",
                    price: 100,
                    included: [
                        "Unlimited handouts",
                        "QR code and link sharing",
                        "Basic analytics",
                        "Scroll depth and reading time",
                        "Custom branding",
                        "Feedback module",
                        "SSO (SAML / SCIM)",
                        "Audit logs",
                        "Priority support",
                    ],
                },
            ],
            features: [
                "Unlimited handouts",
                "QR code and link sharing",
                "Basic analytics",
                "Scroll depth and reading time",
                "Custom branding",
                "Feedback module",
                "SSO (SAML / SCIM)",
                "Audit logs",
                "Priority support",
            ],
            yearlyDiscount: 0.2,
            defaultBilling: "yearly",
            priceSuffix: "per user / month",
            recommendedLabel: "Recommended",
        },
    },
    "roi-calculator": {
        type: "roi-calculator",
        title: "ROI calculator",
        data: {
            team: 25,
            presentations: 4,
            hoursSaved: 0.5,
            hourlyRate: 75,
            seatPrice: 30,
        },
    },
    "before-after-annotated": {
        type: "before-after-annotated",
        title: "Before / after (annotated)",
        data: {
            beforeSrc: "/images/placeholder_1.jpg",
            afterSrc: "/images/placeholder_2.jpg",
            beforeAlt: "Old slide",
            afterAlt: "New handyout",
            annotations: [
                { id: "a1", x: 24, y: 34, title: "One metric instead of a table", detail: "24 cells → 1 hero value" },
                { id: "a2", x: 71, y: 38, title: "Readable font size", detail: "9 pt → 17 pt" },
                { id: "a3", x: 24, y: 78, title: "Action right inside the handout", detail: "No CTA → book a meeting" },
            ],
            beforeLabel: "Before",
            afterLabel: "After",
            aspectRatio: "16 / 10",
            defaultMode: "after",
        },
    },
    "before-after-slider": {
        type: "before-after-slider",
        title: "Before / after (slider)",
        data: {
            beforeSrc: "/images/placeholder_1.jpg",
            afterSrc: "/images/placeholder_2.jpg",
            beforeAlt: "Old slide",
            afterAlt: "New handyout",
            beforeLabel: "Before",
            afterLabel: "After",
            caption: "Slide 14 as a PDF · the same slide as a handyout",
            aspectRatio: "16 / 10",
            defaultPosition: 50,
        },
    },
    "logo-wall": {
        type: "logo-wall",
        title: "Customer logos",
        data: {
            industries: [
                { id: "software", label: "Software", color: "var(--chart-1)" },
                { id: "finance", label: "Finance", color: "var(--chart-2)" },
                { id: "industry", label: "Industry and logistics", color: "var(--chart-3)" },
                { id: "health", label: "Healthcare", color: "var(--chart-4)" },
            ],
            logos: [
                { id: "l1", name: "Stripe", logo: "lndev:stripe", industry: "finance", since: 2019 },
                { id: "l2", name: "Notion", logo: "lndev:notion", industry: "software", since: 2021 },
                { id: "l3", name: "Vercel", logo: "lndev:vercel", industry: "software", since: 2020 },
                { id: "l4", name: "Linear", logo: "lndev:linear", industry: "software", since: 2022 },
                { id: "l5", name: "Figma", logo: "lndev:figma", industry: "software", since: 2021 },
                { id: "l6", name: "GitHub", logo: "lndev:github", industry: "software", since: 2023 },
                { id: "l7", name: "Slack", logo: "lndev:slack", industry: "software", since: 2019 },
                { id: "l8", name: "Airbnb", logo: "lndev:airbnb", industry: "industry", since: 2022 },
                { id: "l9", name: "Spotify", logo: "lndev:spotify", industry: "industry", since: 2020 },
                { id: "l10", name: "Revolut", logo: "lndev:revolut", industry: "finance", since: 2023 },
                { id: "l11", name: "Framer", logo: "lndev:framer", industry: "software", since: 2024 },
                { id: "l12", name: "Bayer", logo: "lndev:bayer", industry: "health", since: 2024 },
            ],
            total: 500,
            totalSuffix: "+",
            totalLabel: "companies use Handyout",
            delta: "+12% vs. last year",
            speed: 30,
            idleHint: "Drag or swipe to browse",
        },
    },
    "keywords-grouped": {
        type: "keywords-grouped",
        title: "Keywords grouped",
        data: {
            groups: [
                { id: "fe", label: "Frontend" },
                { id: "be", label: "Backend" },
                { id: "infra", label: "Infrastructure" },
                { id: "qa", label: "Quality" },
            ],
            items: [
                { id: "react", text: "React", group: "fe", weight: 3, slides: [4, 9, 12], description: "UI library that every surface of the product is built with." },
                { id: "ts", text: "TypeScript", group: "fe", weight: 3, slides: [4, 5, 9, 12, 14], description: "JavaScript with type checking, so errors show up while the code is written." },
                { id: "next", text: "Next.js", group: "fe", weight: 2, slides: [4, 10], description: "Framework around React that renders pages on the server so they load faster." },
                { id: "tailwind", text: "Tailwind CSS", group: "fe", weight: 1, slides: [9], description: "Styling tool that keeps the design rules directly in the code." },
                { id: "pg", text: "PostgreSQL", group: "be", weight: 3, slides: [6, 7, 11], description: "Database for all customer and usage data." },
                { id: "node", text: "Node.js", group: "be", weight: 2, slides: [6, 7], description: "Runtime environment that the API runs on, on the server." },
                { id: "prisma", text: "Prisma", group: "be", weight: 1, slides: [7], description: "Translates queries from the code safely into database commands." },
                { id: "redis", text: "Redis", group: "be", weight: 1, slides: [11], description: "Fast cache for data that is read very often." },
                { id: "aws", text: "AWS Frankfurt", group: "infra", weight: 3, slides: [3, 14, 15], description: "Cloud hosting in Frankfurt, all data stays inside the EU." },
                { id: "docker", text: "Docker", group: "infra", weight: 2, slides: [13, 14], description: "Packages every application together with everything it needs to run." },
                { id: "k8s", text: "Kubernetes", group: "infra", weight: 2, slides: [14], description: "Starts those packages and spreads them across several servers automatically." },
                { id: "tf", text: "Terraform", group: "infra", weight: 1, slides: [14], description: "Describes the entire server landscape as text so it can be rebuilt identically at any time." },
                { id: "playwright", text: "Playwright", group: "qa", weight: 2, slides: [16], description: "Clicks through the app automatically and tests real flows the way a user would." },
                { id: "vitest", text: "Vitest", group: "qa", weight: 1, slides: [16], description: "Fast automated tests for individual functions." },
                { id: "zod", text: "Zod", group: "qa", weight: 1, slides: [5], description: "Validates incoming data against fixed rules before it is processed." },
            ],
            otherLabel: "Other",
        },
    },
    "keywords-explained": {
        type: "keywords-explained",
        title: "Keywords explained",
        data: {
            groups: [
                { id: "fe", label: "Frontend" },
                { id: "be", label: "Backend" },
                { id: "infra", label: "Infrastructure" },
                { id: "qa", label: "Quality" },
            ],
            items: [
                { id: "react", text: "React", group: "fe", weight: 3, slides: [4, 9, 12], description: "UI library that every surface of the product is built with." },
                { id: "ts", text: "TypeScript", group: "fe", weight: 3, slides: [4, 5, 9, 12, 14], description: "JavaScript with type checking, so errors show up while the code is written." },
                { id: "next", text: "Next.js", group: "fe", weight: 2, slides: [4, 10], description: "Framework around React that renders pages on the server so they load faster." },
                { id: "tailwind", text: "Tailwind CSS", group: "fe", weight: 1, slides: [9], description: "Styling tool that keeps the design rules directly in the code." },
                { id: "pg", text: "PostgreSQL", group: "be", weight: 3, slides: [6, 7, 11], description: "Database for all customer and usage data." },
                { id: "node", text: "Node.js", group: "be", weight: 2, slides: [6, 7], description: "Runtime environment that the API runs on, on the server." },
                { id: "prisma", text: "Prisma", group: "be", weight: 1, slides: [7], description: "Translates queries from the code safely into database commands." },
                { id: "redis", text: "Redis", group: "be", weight: 1, slides: [11], description: "Fast cache for data that is read very often." },
                { id: "aws", text: "AWS Frankfurt", group: "infra", weight: 3, slides: [3, 14, 15], description: "Cloud hosting in Frankfurt, all data stays inside the EU." },
                { id: "docker", text: "Docker", group: "infra", weight: 2, slides: [13, 14], description: "Packages every application together with everything it needs to run." },
                { id: "k8s", text: "Kubernetes", group: "infra", weight: 2, slides: [14], description: "Starts those packages and spreads them across several servers automatically." },
                { id: "tf", text: "Terraform", group: "infra", weight: 1, slides: [14], description: "Describes the entire server landscape as text so it can be rebuilt identically at any time." },
                { id: "playwright", text: "Playwright", group: "qa", weight: 2, slides: [16], description: "Clicks through the app automatically and tests real flows the way a user would." },
                { id: "vitest", text: "Vitest", group: "qa", weight: 1, slides: [16], description: "Fast automated tests for individual functions." },
                { id: "zod", text: "Zod", group: "qa", weight: 1, slides: [5], description: "Validates incoming data against fixed rules before it is processed." },
            ],
            allLabel: "All",
        },
    },
};

if (process.env.NODE_ENV !== "production") {
    Object.entries(DEMO_MODULES).forEach(([type, data]) => {
        const result = ModuleTypeSchema.safeParse({ id: "demo-check", ...data });
        if (!result.success) {
            // eslint-disable-next-line no-console
            console.warn(
                `[DEMO_MODULES] "${type}" invalid against ModuleTypeSchema:`,
                result.error.issues,
            );
        }
    });
}
